import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { HelpIndex, RemoveRestoreDocsOperationType, Endpoint, ModalTargetType, UpdateStatusType, MessageType } from '../../../models/enums';

export interface RemoveRestoreDocsModalData {
    operationType: RemoveRestoreDocsOperationType;
    targetType: ModalTargetType;
    targets: Array<any>;
    updateStatusType: UpdateStatusType;
}

@Component({
    standalone: false,
    selector: 'res-remove-restore-docs',
    templateUrl: './remove-restore-docs-modal.component.html',
    styleUrls: ['./remove-restore-docs-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class RemoveRestoreDocsModalComponent extends BaseModalComponent implements OnInit {
    isBusy = false;
    isBusyMessage: string;
    modalTitle: string;
    modalClass = 'remove-restore-docs';
    modalContainerAutomationId: string;
    modalAutomationId: string;
    primaryButtonLabel: string;
    modalHelpIndex: HelpIndex;

    operationType: RemoveRestoreDocsOperationType;
    targetType: ModalTargetType;
    targets: Array<any>;
    updateStatusType: UpdateStatusType;
    comment: string;

    constructor(
        private modalService: BsModalService,
        public editEntityModalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, editEntityModalRef);
    }

    ngOnInit() {
        const removeRestoreDocsModalData = this.modalData as RemoveRestoreDocsModalData;
        this.targetType = removeRestoreDocsModalData.targetType;
        this.operationType = removeRestoreDocsModalData.operationType;
        this.targets = removeRestoreDocsModalData.targets;
        this.updateStatusType = removeRestoreDocsModalData.updateStatusType;

        switch (this.operationType) {
            case RemoveRestoreDocsOperationType.removeFromProjectData: {
                this.modalTitle = 'Remove Documents from Project Data';
                this.primaryButtonLabel = 'Remove';
                this.isBusyMessage = 'Removing documents...';
                this.modalContainerAutomationId = 'message';
                this.modalAutomationId = this.modalContainerAutomationId + '-modal';
                break;
            }
            case RemoveRestoreDocsOperationType.sendToDiscardPile: {
                this.modalTitle = 'Send Documents to Discard Pile';
                this.primaryButtonLabel = 'Send to Discard Pile';
                this.isBusyMessage = 'Removing documents...';
                this.modalAutomationId = 'send-docs-to-discard-pile';
                this.modalHelpIndex = HelpIndex.myedisc_discard_pile_dlg;
                break;
            }
            case RemoveRestoreDocsOperationType.restoreFromDiscardPile: {
                this.modalTitle = 'Restore Documents from Discard Pile';
                this.primaryButtonLabel = 'Restore to Project Data';
                this.isBusyMessage = 'Restoring documents...';
                this.modalAutomationId = 'restore-docs-from-discard-pile';
                this.modalHelpIndex = HelpIndex.myedisc_discard_pile_dlg;
                break;
            }
            case RemoveRestoreDocsOperationType.deleteFilesFromProject: {
                this.modalTitle = 'Delete Files from Project';
                this.primaryButtonLabel = 'Delete';
                this.isBusyMessage = 'Deleting documents...';
                this.modalContainerAutomationId = 'message';
                this.modalAutomationId = this.modalContainerAutomationId + '-modal';
                break;
            }
        }
    }

    okEnabled(): boolean {
        return Boolean(this.targets && this.primaryButtonLabel) && !this.isBusy && 
        ((this.operationType !==  RemoveRestoreDocsOperationType.sendToDiscardPile && this.operationType !== RemoveRestoreDocsOperationType.restoreFromDiscardPile) ||
        ((this.operationType ===  RemoveRestoreDocsOperationType.sendToDiscardPile || this.operationType === RemoveRestoreDocsOperationType.restoreFromDiscardPile) && this.comment && this.comment?.trim() !== ''));
    }

    ok(): void {
        let endpoint: Endpoint;
        let request: any;

        switch (this.operationType) {
            case RemoveRestoreDocsOperationType.removeFromProjectData: {
                let viewHandle = this.targets[0].view ? this.targets[0].view.handle : this.targets[0].handle;
                if (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn) {
                    viewHandle = this.commonService.matrixGridsSelection?.viewHandle;
                }
                request = {
                    documentsHandleList: this.targetType === ModalTargetType.document ? this.targets : undefined,
                    srcViewHandle: this.targetType === ModalTargetType.view ? viewHandle : undefined,
                    targetViewHandle: this.commonService.projectData.handle,
                    taskDescription: `Removing ${this.targetType === ModalTargetType.view ? 'all ' : ''}documents from Project Data`
                };
                endpoint = this.targetType === ModalTargetType.document ?
                    Endpoint.viewManager_removeFromDocuments : Endpoint.viewManager_removeFromView;
                break;
            }
            case RemoveRestoreDocsOperationType.sendToDiscardPile: {
                request = {
                    documentHandles: this.targetType === ModalTargetType.document ? this.targets : undefined,
                    viewHandle: this.targetType === ModalTargetType.view ? (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? this.commonService.matrixGridsSelection?.viewHandle : this.targets[0]?.view ? this.targets[0].view.handle :  this.targets[0].handle) : undefined,
                    comments: this.comment,
                    projectHandle: this.commonService.currentProject.handle,
                    taskDescription: `Sending documents to Discard Pile`
                };
                endpoint = this.targetType === ModalTargetType.document ?
                    Endpoint.projectManager_removeDocumentsFromProject : Endpoint.projectManager_removeSearchResultsFromProject;
                break;
            }
            case RemoveRestoreDocsOperationType.restoreFromDiscardPile: {
                request = {
                    documentHandles: this.targetType === ModalTargetType.document ? this.targets : undefined,
                    viewHandle: this.targetType === ModalTargetType.view ? (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? this.commonService.matrixGridsSelection?.viewHandle : this.targets[0].handle) : undefined,
                    comments: this.comment,
                    projectHandle: this.commonService.currentProject.handle,
                    taskDescription: `Restoring ${this.targetType === ModalTargetType.view ? 'all ' : ''}documents to ${this.commonService.projectData.name}`
                };
                endpoint = this.targetType === ModalTargetType.document ?
                    Endpoint.projectManager_restoreDocumentsToProject : Endpoint.projectManager_restoreSearchResultsToProject;
                break;
            }
            case RemoveRestoreDocsOperationType.deleteFilesFromProject: {
                request = {
                    documentHandleList: this.targetType === ModalTargetType.document ? this.targets : undefined,
                    viewHandle: this.targetType === ModalTargetType.view ? (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? this.commonService.matrixGridsSelection?.viewHandle : this.targets[0].handle) : undefined,
                    taskDescription: `Deleting ${this.targetType === ModalTargetType.view ? 'all ' : ''}documents from Project`
                };
                endpoint = this.targetType === ModalTargetType.document ?
                    Endpoint.corpusManager_purgeFromDocuments : Endpoint.corpusManager_purgeFromView;
                break;
            }
        }
        if (endpoint && request) {
            this.isBusy = true;
            this.restService.post(endpoint, request, true, [MessageType.entityChanged, this.updateStatusType])
                .then(response => {
                    if (response.errorCode) {
                        this.commonService.openErrorModal(response.extendedStatus);
                    }
                });
            this.isBusy = false;
            this.closeModal();
        }
    }
}
